import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  setFirstLogin,
} from '../../store/slices/authSlice';
import authApi from 'api/authApi';
import { Shield, Mail, Lock, Eye, EyeOff, Loader2, Building2, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../../components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import Logo1 from 'assets/logo1.png';
import LoginBg from 'assets/login-bg.jpg';
import bgImage from 'assets/images/loginBgImage.png';
import { getFcmToken } from 'utlis/fcmToken';
import timezones from "timezones-list";
import { getDeviceId, getDeviceName  } from "utlis/helperFunction";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated, isLoading } = useSelector((state) => state?.auth);

  const [formData, setFormData] = useState({
    emailOrCode: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });

  /* --------------------------------------------------------------- */
  /* 1. Auto-fill saved email/code on mount                          */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    // "Remember me" used to keep the password in localStorage in plain text, where any
    // script or extension could read it. Only the login ID is remembered now — the
    // browser's own password manager handles the rest. Clear anything left behind.
    try { localStorage.removeItem('savePassword'); } catch { /* storage disabled */ }

    const savedIdentifier = localStorage.getItem('savedIdentifier') || sessionStorage.getItem('savedIdentifier');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedIdentifier) {
      setFormData(prev => ({ ...prev, emailOrCode: savedIdentifier }));
      setRememberMe(savedRememberMe);
    }
  }, []);

  // Logged out automatically because the login hours ended → say why.
  useEffect(() => {
    let reason = '';
    try { reason = sessionStorage.getItem('login_block_reason') || ''; sessionStorage.removeItem('login_block_reason'); } catch { /* */ }
    if (reason) { toast.error(reason, { duration: 8000 }); setErrors({ general: reason }); }
  }, []);

  /* --------------------------------------------------------------- */
  /* 2. Redirect if already logged in                                 */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    if (isAuthenticated) {
      toast.success('Welcome back! 🎉');
      // Come back to whatever page the expired session was on, so a shared link isn't lost.
      let back = '';
      try {
        back = sessionStorage.getItem('postLoginRedirect') || '';
        sessionStorage.removeItem('postLoginRedirect');
      } catch { /* storage disabled */ }
      const safe = back && back.startsWith('/') && !back.startsWith('//') && !back.startsWith('/login');
      navigate(safe ? back : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /* --------------------------------------------------------------- */
  /* 3. Validation                                                    */
  /* --------------------------------------------------------------- */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.emailOrCode.trim()) newErrors.emailOrCode = 'Email or Employee Code is required';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* --------------------------------------------------------------- */
  /* 4. Submit → Save identifier based on "Remember Me"               */
  /* --------------------------------------------------------------- */
  const kolkataTz = timezones.find((tz) => tz.tzCode === "Asia/Kolkata");

  const handleSubmit = async (e) => {
    e.preventDefault();

    dispatch(loginStart());

    try {
      const deviceId = getDeviceId();
      const deviceName = getDeviceName();
      const fcmToken = await getFcmToken();
      // console.log(("get fcm token", fcmToken));
      const timezone = kolkataTz?.tzCode || "Asia/Kolkata";

      const apiResponse = await authApi.login({
        identifier: formData.emailOrCode,
        password: formData.password,
        fcm_token: fcmToken,
        timezone: timezone,
        platform: 'web',
        device_name: deviceName || 'web',
        device_id: deviceId || '1234567890abcdef',
      });

      if (!apiResponse.user.is_active) {
        dispatch(loginFailure("Your account is inactive. Please contact admin."));
        toast.error("Your account is inactive. Please contact admin.");
        return;
      }

      const user = {
        id: apiResponse.user.id,
        firstName: apiResponse.user.first_name?.trim() || "User",
        lastName: apiResponse.user.last_name?.trim() || "",
        email: apiResponse.user.email,
        code: apiResponse.user.code,
        role: apiResponse.user.role?.name || "user",
        profileImage: apiResponse.user.profile_img || null,
        isSuperuser: apiResponse.user.is_superuser,
        isStaff: apiResponse.user.is_staff,
      };

      dispatch(
        loginSuccess({
          user,
          token: apiResponse.access_token,
          refreshToken: apiResponse.refresh_token,
          isFirstLogin: apiResponse.user.is_first_login,
          employee: apiResponse.user,
        })
      );

      if (rememberMe) {
        localStorage.setItem("savedIdentifier", formData.emailOrCode);
        localStorage.setItem("rememberMe", "true");
      } else {
        sessionStorage.setItem("savedIdentifier", formData.emailOrCode);
        localStorage.removeItem("savedIdentifier");
        localStorage.removeItem("savePassword");
        localStorage.removeItem("rememberMe");
      }

    } catch (err) {
      const message =
        err.response?.data.error ||
        err.response?.data?.message ||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.identifier?.[0] ||
        err.response?.data?.password?.[0] ||
        err.message ||
        "Invalid credentials.";

      dispatch(loginFailure(message));
      toast.error(message);
      setErrors({ general: message });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="relative min-h-screen w-full flex bg-gradient-to-br from-slate-50 via-white to-slate-100">

      {/* Laptops and large screens (lg and up): the watercolour fills the screen and the
          sign-in sits in one glass card in the centre. Tablets and phones keep the plain
          layout below — they use the mobile app. */}
      <div aria-hidden="true" className="hidden lg:block absolute inset-0 bg-[#fdf1dc] bg-cover bg-center"
        style={{ backgroundImage: `url(${LoginBg})` }} />
      <div aria-hidden="true" className="hidden lg:block absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(255,255,255,.55), rgba(255,255,255,0) 62%)' }} />

      {/* RIGHT SECTION - Login Form */}
      <div className="relative w-full flex flex-col items-center justify-center p-4 sm:p-6 lg:p-10">
        <div className="w-full max-w-md space-y-8 lg:space-y-5 lg:max-w-[420px] 2xl:max-w-[460px] lg:rounded-[20px] lg:border lg:border-white/90 lg:bg-white/70 lg:backdrop-blur-md lg:px-9 lg:py-8 2xl:px-11 2xl:py-10 lg:shadow-[0_18px_50px_rgba(120,80,30,0.16)]">

          {/* Logo (inside the card on laptops / large screens, with a thin gold rule) */}
          <div className="flex flex-col items-center space-y-4 mb-4 lg:mb-0 lg:space-y-3">
            <img src={Logo1} alt="Mohilya" fetchPriority="high" className="max-w-[200px] lg:max-w-[150px] 2xl:max-w-[175px] h-auto" />
            <span className="hidden lg:block w-12 h-0.5 rounded bg-[#b8892f]/80" />
          </div>

          {/* Welcome Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 lg:text-[26px] 2xl:text-[30px] lg:font-normal lg:tracking-wide lg:text-[#5b1e1e] lg:[font-family:Marcellus,Georgia,'Times_New_Roman',serif]">Welcome back</h1>
            <p className="text-slate-500 lg:text-[13px] lg:text-[#8a7767]">Sign in to your account to continue</p>
          </div>

          {/* Login Card */}
          <Card className="border-0 shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm lg:shadow-none lg:bg-transparent lg:backdrop-blur-none">
            <CardContent className="pt-6 border rounded-lg lg:border-0 lg:p-0 lg:pt-1">
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Email / Code Input */}
                <div className="space-y-2">
                  <Label htmlFor="emailOrCode" className="text-sm font-medium text-slate-700">
                    Email or Employee Code
                  </Label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2a3646] transition-colors">
                      <Mail className="h-4 w-4" />
                    </div>
                    <Input
                      id="emailOrCode"
                      name="emailOrCode"
                      type="text"
                      autoComplete="username"
                      value={formData.emailOrCode}
                      onChange={handleChange}
                      onFocus={() => setIsFocused(prev => ({ ...prev, email: true }))}
                      onBlur={() => setIsFocused(prev => ({ ...prev, email: false }))}
                      placeholder="admin@company.com or EMP001"
                      className={cn(
                        "pl-11 h-12 border-slate-200 focus:border-[#2a3646] focus:ring-[#2a3646]/20 transition-all duration-200 placeholder:text-gray-300 placeholder:text-sm lg:border-[#ecdcc6] lg:bg-white/90 lg:focus:border-[#b8892f] lg:focus:ring-[#b8892f]/20",
                        errors.emailOrCode && "border-destructive focus:border-destructive focus:ring-destructive/20",
                        (isFocused.email || formData.emailOrCode) && "border-[#2a3646] lg:border-[#b8892f]"
                      )}
                    />
                    {formData.emailOrCode && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, emailOrCode: '' }))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {errors.emailOrCode && (
                    <p className="text-sm text-destructive flex items-center gap-1 animate-slide-up">
                      <span className="inline-block w-1 h-1 bg-destructive rounded-full" />
                      {errors.emailOrCode}
                    </p>
                  )}
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2a3646] transition-colors">
                      <Lock className="h-4 w-4" />
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setIsFocused(prev => ({ ...prev, password: true }))}
                      onBlur={() => setIsFocused(prev => ({ ...prev, password: false }))}
                      placeholder="••••••••"
                      className={cn(
                        "pl-11 pr-11 h-12 border-slate-200 focus:border-[#2a3646] focus:ring-[#2a3646]/20 transition-all duration-200 placeholder:text-gray-300 placeholder:text-sm lg:border-[#ecdcc6] lg:bg-white/90 lg:focus:border-[#b8892f] lg:focus:ring-[#b8892f]/20",
                        errors.password && "border-destructive focus:border-destructive focus:ring-destructive/20",
                        (isFocused.password || formData.password) && "border-[#2a3646] lg:border-[#b8892f]"
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-destructive flex items-center gap-1 animate-slide-up">
                      <span className="inline-block w-1 h-1 bg-destructive rounded-full" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Remember Me + Forgot Password */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center space-x-2.5">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked)}
                      className="border-slate-300 data-[state=checked]:bg-[#2a3646] data-[state=checked]:border-[#2a3646]"
                    />
                    <Label
                      htmlFor="remember"
                      className="text-sm font-normal text-slate-600 cursor-pointer select-none hover:text-slate-900 transition-colors"
                    >
                      Remember me
                    </Label>
                  </div>
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-sm font-medium text-[#2a3646] hover:text-[#1a2332] p-0 h-auto underline-offset-4 hover:underline"
                  >
                    Forgot password?
                  </Button>
                </div>

                {/* General Error Alert */}
                {errors.general && (
                  <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 animate-slide-down">
                    <p className="text-sm text-destructive font-medium flex items-start gap-2">
                      <span className="mt-0.5">⚠️</span>
                      {errors.general}
                    </p>
                  </div>
                )}

                {/* Sign In Button */}
                <Button
                  type="submit"
                  className={cn(
                    "w-full h-12 text-base font-semibold bg-gradient-to-r from-[#750000] to-[#750000] hover:from-[#920404] hover:to-[#920404] text-[#f4b200] shadow-lg shadow-[#750000]/25 hover:shadow-[#750000]/40 transition-all duration-300 lg:from-[#6a2424] lg:to-[#5b1e1e] lg:hover:from-[#752a2a] lg:hover:to-[#662222] lg:text-white lg:tracking-wide",
                    "disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:shadow-[#2a3646]/25"
                  )}
                  disabled={
                    isLoading ||
                    !formData.emailOrCode.trim() ||
                    !formData.password.trim()
                  }
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    "Sign In"
                  )}
                </Button>

              </form>
            </CardContent>

            {/* Card Footer - Optional Helper Text */}
            {/* <CardFooter className="flex justify-center pb-6">
              <p className="text-xs text-slate-400 text-center">
                By signing in, you agree to our{" "}
                <a href="/terms" className="text-[#2a3646] hover:underline">Terms</a>
                {" "}&{" "}
                <a href="/privacy" className="text-[#2a3646] hover:underline">Privacy Policy</a>
              </p>
            </CardFooter> */}
          </Card>

          {/* Footer (tablet / phone: under the form) */}
          <p className="text-center text-xs text-slate-500 lg:hidden pt-4">
            © {new Date().getFullYear()} Mohilya. Powered by{" "}
            <a
              href="https://www.ammrs.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2a3646] hover:underline"
            >
              AMMRS
            </a>.
          </p>

        </div>

        <p className="hidden lg:block relative mt-6 text-center text-xs text-[#8a7767]">
          © {new Date().getFullYear()} Mohilya. Powered by{" "}
          <a href="https://www.ammrs.in/" target="_blank" rel="noopener noreferrer"
            className="font-semibold text-[#5b1e1e] hover:underline">AMMRS</a>
        </p>
      </div>

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .animate-slide-up { animation: slide-up 0.3s ease-out forwards; }
        .animate-slide-down { animation: slide-down 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default LoginPage;